import { useCallback, useEffect, useState } from 'react';
import { IntlShape } from 'react-intl';
import { useIntl } from '@tink/core/src/components/providers/LanguageProvider/useIntl';
import { ProviderField } from '../../models/provider';
import { ProviderInputState } from '../screens/SubmitCredentials/molecules/ProviderInputs/Input/Input';
import { calculateProviderInputs } from '../screens/SubmitCredentials/PersistedProviderInputsProvider/PersistedProviderInputsProvider';
import { ProviderInputValidator } from '../screens/SubmitCredentials/molecules/ProviderInputs/ProviderInputValidator';
import { getErrorMessage } from './templates';

const updateInputState = (
  providerField: ProviderField,
  currentState: ProviderInputState,
  value: string,
  intl: IntlShape
) => {
  const validation = new ProviderInputValidator(providerField).validate(value);

  const onFormSubmit = true;

  const providerInputState: ProviderInputState = {
    ...(onFormSubmit
      ? {
          ...validation.onSubmit,
          errorMessage: getErrorMessage(validation.onSubmit, intl),
        }
      : {
          ...validation.onType,
          errorMessage: getErrorMessage(validation.onType, intl),
        }),
    value,
    disabled: currentState.disabled,
    providerField: currentState.providerField,
    showCheckIcon: Boolean(providerField.pattern && validation.onSubmit.isValid),
  };

  return providerInputState;
};

type ProviderFormState = Record<string, ProviderInputState>;
type OnChangeProviderField = (name: string, value: string) => void;
type UseProviderInputReturnType = {
  formState: ProviderFormState;
  updateField: OnChangeProviderField;
};

export const useProviderInput = (fields: ProviderField[]): UseProviderInputReturnType => {
  const intl = useIntl();

  const [formState, setFormState] = useState<Record<string, ProviderInputState>>({});

  useEffect(() => {
    // Setup initial values
    const inputFields = fields.filter(
      (field) => field.type === 'INPUT' && field.style !== 'POSITIONAL_INPUT'
    );
    setFormState(calculateProviderInputs(inputFields));
  }, [fields]);

  const updateField = useCallback(
    (name: string, value: string) => {
      setFormState((prevFormState) => ({
        ...prevFormState,
        [name]: {
          ...updateInputState(prevFormState[name].providerField, prevFormState[name], value, intl),
        },
      }));
    },
    [intl]
  );

  return { formState, updateField };
};
